import React from 'react';

const BillingOverview = ({
  subscriptionDetails,
  billingDetails,
  subscriptionActive,
}) => {
  // Helper function to calculate remaining days
  const getRemainingDays = (validUpto) => {
    if (!validUpto) return 'N/A';

    const today = new Date();
    const validDate = new Date(validUpto);
    const millisecondsDiff = validDate.getTime() - today.getTime();
    const remainingDays = Math.max(
      0,
      Math.ceil(millisecondsDiff / (1000 * 60 * 60 * 24)),
    );

    return remainingDays;
  };

  // Helper function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);

    if (isNaN(date.getTime())) return 'Invalid Date';

    try {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
    } catch (e) {
      return date.toDateString();
    }
  };

  // Helper function to format discount text
  const getDiscountText = (discount) => {
    if (!discount || !discount.valid) return null;

    const { discount_type, discount_value, currency, duration, duration_in_months } = discount;
    
    let discountAmount;
    if (discount_type === 'percent') {
      discountAmount = `${discount_value}% off`;
    } else {
      const formattedAmount = (discount_value / 100).toFixed(2);
      discountAmount = `$${formattedAmount} off`;
    }

    let durationText = '';
    if (duration === 'once') {
      durationText = 'on your first invoice';
    } else if (duration === 'repeating') {
      durationText = `for ${duration_in_months} month${duration_in_months > 1 ? 's' : ''}`;
    } else if (duration === 'forever') {
      durationText = 'for the lifetime of your subscription';
    }

    return `${discountAmount} ${durationText}`;
  };

  // Only show for trial status - use billingDetails.status (fresh from Stripe) instead of subscriptionDetails.status (from Cognito cache)
  if (billingDetails?.status !== 'trialing') {
    return null;
  }

  const planName = billingDetails?.plan_name || 'Plan';
  const remainingDays = getRemainingDays(billingDetails?.plan_valid_upto);
  const startDate = formatDate(billingDetails?.plan_valid_upto);
  const isPaused = !subscriptionActive;
  const discount = billingDetails?.discount;
  const hasDiscount = discount && discount.valid;

  return (
    <div className="billing-overview-card">
      <h2>Billing Overview</h2>
      
      <h3 className="plan-name">
        {planName} — Trial
      </h3>
      
      <p className="trial-info">
        Trial ends in <strong>{remainingDays} days</strong>
      </p>
      
      {hasDiscount && (
        <div className="discount-badge" style={{
          backgroundColor: '#22C55E15',
          border: '1px solid #22C55E',
          borderRadius: '6px',
          padding: '12px 16px',
          marginTop: '16px',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 0L12.2451 7.75486H20L13.8775 12.2451L16.1225 20L10 15.5098L3.87746 20L6.12254 12.2451L0 7.75486H7.75486L10 0Z" fill="#22C55E"/>
            </svg>
            <div>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#16A34A' }}>
                Discount Applied
              </p>
              <p style={{ margin: 0, fontSize: '13px', color: '#15803D', marginTop: '4px' }}>
                You're saving {getDiscountText(discount)}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {isPaused ? (
        <p className="trial-info">
          Your subscription is currently <strong>paused</strong>. Resume your 
          subscription to continue after the trial ends on <strong>{startDate}</strong>.
        </p>
      ) : (
        <p className="trial-info">
          Your paid subscription will begin automatically on{' '}
          <strong>{startDate}</strong>
        </p>
      )}
    </div>
  );
};

export default BillingOverview;
